import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { previewPublicResults } from "../../api/clientVendorDirectoryRankingApi";
import ClientVendorDirectorySubnav from "../../components/casamap/admin/ClientVendorDirectorySubnav";
import ClientVendorDirectoryRankingSubnav from "../../components/casamap/admin/ClientVendorDirectoryRankingSubnav";
import { ClientPortalError, ClientPortalLoading } from "../../components/clientPortal/ClientPortalStates";
import { useLocale } from "../../intl";
import "../../styles/casamapaVendorDirectoryAdmin.css";

const COPY_PREFIX = "realEstate.casaMapa.admin.vendorDirectoryWorkspace.ranking.preview";
const PREVIEW_CATEGORIES = ["plumbing", "electrical", "cleaning", "hvac", "moving"];

export default function ClientVendorDirectoryRankingPreviewPage() {
  const { clientViewId } = useParams();
  const { t } = useLocale();
  const [categoryKey, setCategoryKey] = useState("plumbing");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErrorMessage("");
    try {
      const body = await previewPublicResults(clientViewId, { categoryKey });
      setPreview(body);
    } catch (error) {
      setErrorMessage(error.message || t(`${COPY_PREFIX}.loadError`));
    } finally {
      setLoading(false);
    }
  }, [clientViewId, categoryKey, t]);

  useEffect(() => {
    load();
  }, [load]);

  const organic = preview?.composed?.organicResults || [];
  const featured = preview?.composed?.featuredPlacements || [];
  const sponsored = preview?.composed?.sponsoredPlacements || [];

  return (
    <div className="page-content cvd-admin">
      <ClientVendorDirectorySubnav clientViewId={clientViewId} />
      <ClientVendorDirectoryRankingSubnav clientViewId={clientViewId} />
      <h1 className="cvd-admin__title">{t(`${COPY_PREFIX}.title`)}</h1>
      <div className="cvd-filters">
        <div className="cvd-field">
          <label htmlFor="preview-category">{t(`${COPY_PREFIX}.category`)}</label>
          <select id="preview-category" value={categoryKey} onChange={(event) => setCategoryKey(event.target.value)}>
            {PREVIEW_CATEGORIES.map((value) => (
              <option key={value} value={value}>
                {t(`${COPY_PREFIX}.categories.${value}`)}
              </option>
            ))}
          </select>
        </div>
      </div>
      {loading ? <ClientPortalLoading message={t(`${COPY_PREFIX}.loading`)} /> : null}
      {errorMessage ? <ClientPortalError message={errorMessage} onRetry={load} /> : null}
      {!loading && !errorMessage && preview ? (
        <>
          <div className="cvd-panel">
            <h2>{t(`${COPY_PREFIX}.organic`, { count: organic.length })}</h2>
            <ul>
              {organic.map((item) => (
                <li key={item.vendorId}>
                  {t(`${COPY_PREFIX}.organicPosition`, { position: item.organicPosition ?? "—" })}
                  {item.isTopVendor ? " · Top Vendor" : ""}
                </li>
              ))}
            </ul>
          </div>
          <div className="cvd-panel">
            <h2>{t(`${COPY_PREFIX}.featured`, { count: featured.length })}</h2>
            <ul>
              {featured.map((item, index) => (
                <li key={`${item.vendorId}-${index}`}>
                  {t(`${COPY_PREFIX}.priority`, {
                    label: item.label || t(`${COPY_PREFIX}.featuredFallback`),
                    position: item.displayPosition,
                  })}
                </li>
              ))}
            </ul>
          </div>
          <div className="cvd-panel">
            <h2>{t(`${COPY_PREFIX}.sponsored`, { count: sponsored.length })}</h2>
            <ul>
              {sponsored.map((item, index) => (
                <li key={`${item.vendorId}-${index}`}>
                  {t(`${COPY_PREFIX}.priority`, {
                    label: item.label || t(`${COPY_PREFIX}.sponsoredFallback`),
                    position: item.displayPosition,
                  })}
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : null}
    </div>
  );
}
